import { avatarColor, fmtChannel, fmtDateFull, convStatusLabel } from '@/utils/contactsFormat'

/** Escapa HTML — compartilhado por todo builder deste arquivo. */
export function escapeHtml(s: string): string {
  return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;')
}

/**
 * Gera o HTML da linha do tempo de mensagens (bolhas de chat + separadores de
 * data + atividades) — extraído de buildConvHtml pra ser reaproveitado por
 * outros exports que também precisam de um transcript (ex: Relatório de
 * Resolução), sem duplicar a lógica de bolha/anexo/atividade.
 */
export function buildTimelineHtml(messages: any[], contactNameFallback: string): string {
  const e = escapeHtml
  let lastDate = ''
  return messages.map(m => {
    const type       = m.message_type   // 0=incoming 1=outgoing 2=activity 3=template
    const isActivity = type === 2
    const isOut      = type === 1 || type === 3
    const senderName = m.sender?.name || m.sender?.available_name || (isOut ? 'Agente' : contactNameFallback)
    const ts         = new Date(m.created_at * 1000)
    const timeStr    = ts.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    const dateStr    = ts.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
    const content    = (m.content || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/\n/g,'<br>')

    let sep = ''
    if (dateStr !== lastDate) {
      sep       = `<div class="date-sep"><span>${dateStr.charAt(0).toUpperCase() + dateStr.slice(1)}</span></div>`
      lastDate  = dateStr
    }

    let attachHtml = ''
    if (m.attachments?.length) {
      attachHtml = m.attachments.map((att: any) => {
        if (att.file_type === 'image') return `<div class="att-img-wrap"><img src="${e(att.data_url || '')}" class="att-img" loading="lazy" /></div>`
        if (att.file_type === 'audio') return `<div class="att-file">&#127925; &nbsp;Áudio</div>`
        if (att.file_type === 'video') return `<div class="att-file">&#127909; &nbsp;Vídeo</div>`
        return `<div class="att-file">&#128206; &nbsp;<a href="${e(att.data_url || '')}" target="_blank">Arquivo</a></div>`
      }).join('')
    }

    if (isActivity) {
      return sep + `<div class="msg-activity"><span class="activity-text">${content || e(senderName)}</span><span class="activity-time">${timeStr}</span></div>`
    }

    const avColor  = avatarColor(senderName)
    const avLetter = senderName.charAt(0).toUpperCase()
    const avHtml   = m.sender?.thumbnail
      ? `<img src="${e(m.sender.thumbnail)}" class="bubble-av bubble-av--img" />`
      : `<div class="bubble-av" style="background:${avColor}">${e(avLetter)}</div>`

    return sep + `
    <div class="msg-row ${isOut ? 'msg-row--out' : 'msg-row--in'}">
      ${!isOut ? avHtml : ''}
      <div class="bubble ${isOut ? 'bubble--out' : 'bubble--in'}">
        <div class="bubble-sender">${e(senderName)}</div>
        ${content ? `<div class="bubble-content">${content}</div>` : ''}
        ${attachHtml}
        <div class="bubble-time">${timeStr}</div>
      </div>
      ${isOut ? avHtml : ''}
    </div>`
  }).join('\n')
}

/** Gera o documento HTML autocontido (com CSS embutido) da transcrição de uma conversa exportada. */
export function buildConvHtml(conv: any, messages: any[], contactNameFallback: string): string {
  const contact    = conv.meta?.sender   || {}
  const assignee   = conv.meta?.assignee
  const team       = conv.meta?.team
  const channel    = fmtChannel(conv.meta?.channel || '')
  const statusLbl  = convStatusLabel(conv.status)
  const openedAt   = conv.created_at ? fmtDateFull(conv.created_at) : '—'
  const resolvedAt = conv.status === 'resolved' && conv.last_activity_at ? fmtDateFull(conv.last_activity_at) : null
  const exportedAt = new Date().toLocaleString('pt-BR')
  const contactName = contact.name || contactNameFallback || 'Contato'
  const totalMsgs  = messages.filter(m => m.message_type !== 2).length

  const e = escapeHtml
  const msgsHtml = buildTimelineHtml(messages, contactName)

  const statusClass = { resolved: 'status-resolved', open: 'status-open', pending: 'status-pending', snoozed: 'status-snoozed' }[conv.status as string] || 'status-open'

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Conversa #${conv.id} — ISBET</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;background:#f4f5f7;color:#1f2937;line-height:1.5}
.page-wrap{max-width:780px;margin:0 auto;padding:28px 16px 56px}
/* Header */
.page-header{background:#BC0C24;color:#fff;border-radius:14px;padding:24px 28px;margin-bottom:20px}
.brand{display:flex;align-items:center;gap:10px;margin-bottom:16px}
.brand-name{font-size:18px;font-weight:800;letter-spacing:1px}
.brand-sub{font-size:10px;background:rgba(255,255,255,.2);padding:2px 9px;border-radius:20px;font-weight:600}
.conv-title{font-size:22px;font-weight:700;margin-bottom:14px}
.conv-id-tag{background:rgba(255,255,255,.2);padding:2px 10px;border-radius:6px;font-size:18px}
.meta-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(155px,1fr));gap:10px}
.meta-item{background:rgba(255,255,255,.12);border-radius:10px;padding:10px 13px}
.meta-lbl{font-size:9px;text-transform:uppercase;letter-spacing:1px;opacity:.65;margin-bottom:3px;font-weight:600}
.meta-val{font-size:13px;font-weight:600}
.status-badge{display:inline-block;padding:2px 10px;border-radius:20px;font-size:11px;font-weight:700;text-transform:uppercase}
.status-resolved{background:#dcfce7;color:#166534}
.status-open{background:#dbeafe;color:#1e40af}
.status-pending{background:#fef9c3;color:#854d0e}
.status-snoozed{background:#f3f4f6;color:#6b7280}
/* Stats */
.stats-bar{display:flex;gap:12px;margin-bottom:20px}
.stat-box{background:#fff;border-radius:10px;padding:12px 16px;flex:1;text-align:center;box-shadow:0 1px 3px rgba(0,0,0,.07)}
.stat-n{font-size:24px;font-weight:800;color:#BC0C24}
.stat-l{font-size:11px;color:#6b7280;margin-top:2px;font-weight:500}
/* Timeline */
.timeline{display:flex;flex-direction:column;gap:4px}
/* Date separator */
.date-sep{text-align:center;margin:18px 0 10px}
.date-sep span{background:#e5e7eb;color:#6b7280;font-size:11px;padding:4px 14px;border-radius:20px;font-weight:600}
/* Message rows */
.msg-row{display:flex;align-items:flex-end;gap:8px;max-width:78%}
.msg-row--in{align-self:flex-start}
.msg-row--out{align-self:flex-end;flex-direction:row-reverse}
/* Avatar */
.bubble-av{width:30px;height:30px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;color:#fff;font-size:11px;font-weight:700}
.bubble-av--img{object-fit:cover}
/* Bubble */
.bubble{padding:9px 13px;border-radius:14px;max-width:100%;word-break:break-word}
.bubble--in{background:#fff;border-radius:14px 14px 14px 3px;box-shadow:0 1px 3px rgba(0,0,0,.07)}
.bubble--out{background:#BC0C24;color:#fff;border-radius:14px 14px 3px 14px}
.bubble-sender{font-size:10px;font-weight:700;margin-bottom:3px;opacity:.65}
.bubble-content{font-size:13.5px;line-height:1.55}
.bubble-time{font-size:10px;opacity:.5;margin-top:5px;text-align:right}
/* Attachments */
.att-img-wrap{margin-top:6px;border-radius:8px;overflow:hidden;max-width:260px}
.att-img{width:100%;display:block}
.att-file{display:inline-flex;align-items:center;gap:6px;background:rgba(0,0,0,.07);border-radius:6px;padding:5px 10px;font-size:12px;margin-top:5px}
.att-file a{color:inherit}
.bubble--out .att-file{background:rgba(255,255,255,.18)}
/* Activity */
.msg-activity{align-self:center;text-align:center;margin:6px 0}
.activity-text{background:rgba(0,0,0,.06);color:#6b7280;font-size:11.5px;padding:4px 14px;border-radius:20px;display:inline-block}
.activity-time{display:block;font-size:10px;color:#9ca3af;margin-top:3px}
/* Footer */
.page-footer{text-align:center;margin-top:36px;font-size:11px;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:16px}
/* Print / PDF */
@media print{
  body{background:#fff}
  .page-wrap{padding:0;max-width:100%}
  .page-header{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .bubble--out{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .stat-n{color:#BC0C24!important;-webkit-print-color-adjust:exact;print-color-adjust:exact}
}
</style>
</head>
<body>
<div class="page-wrap">

  <div class="page-header">
    <div class="brand">
      <span class="brand-name">ISBET</span>
      <span class="brand-sub">Wootdash CRM</span>
    </div>
    <div class="conv-title">Conversa <span class="conv-id-tag">#${conv.id}</span></div>
    <div class="meta-grid">
      <div class="meta-item">
        <div class="meta-lbl">Status</div>
        <div class="meta-val"><span class="status-badge ${statusClass}">${statusLbl}</span></div>
      </div>
      <div class="meta-item">
        <div class="meta-lbl">Contato</div>
        <div class="meta-val">${e(contactName)}</div>
      </div>
      <div class="meta-item">
        <div class="meta-lbl">Agente</div>
        <div class="meta-val">${e(assignee?.name || 'Não atribuído')}</div>
      </div>
      ${team ? `<div class="meta-item"><div class="meta-lbl">Equipe</div><div class="meta-val">${e(team.name)}</div></div>` : ''}
      <div class="meta-item">
        <div class="meta-lbl">Canal</div>
        <div class="meta-val">${e(channel)}</div>
      </div>
      <div class="meta-item">
        <div class="meta-lbl">Aberto em</div>
        <div class="meta-val">${e(openedAt)}</div>
      </div>
      ${resolvedAt ? `<div class="meta-item"><div class="meta-lbl">Resolvido em</div><div class="meta-val">${e(resolvedAt)}</div></div>` : ''}
    </div>
  </div>

  <div class="stats-bar">
    <div class="stat-box">
      <div class="stat-n">${totalMsgs}</div>
      <div class="stat-l">mensagens</div>
    </div>
    <div class="stat-box">
      <div class="stat-n">${messages.filter(m => m.message_type === 0).length}</div>
      <div class="stat-l">do contato</div>
    </div>
    <div class="stat-box">
      <div class="stat-n">${messages.filter(m => m.message_type === 1 || m.message_type === 3).length}</div>
      <div class="stat-l">do agente/bot</div>
    </div>
    <div class="stat-box">
      <div class="stat-n">${messages.filter(m => m.attachments?.length).length}</div>
      <div class="stat-l">com anexos</div>
    </div>
  </div>

  <div class="timeline">
    ${msgsHtml}
  </div>

  <div class="page-footer">
    Exportado em ${exportedAt} &nbsp;·&nbsp; ISBET Wootdash CRM &nbsp;·&nbsp; Conversa #${conv.id}
    <br><small>Para salvar como PDF: Ctrl+P → Salvar como PDF</small>
  </div>

</div>
</body>
</html>`
}

export interface ResolutionDetailExportData {
  convId: number
  status: string
  contactName: string
  teamName: string | null
  assigneeName: string | null
  createdAt: number
  resolvedAt: number
  categoryLabel: string
  bizCategoryLabel: string
  totalDuration: string
  longestGap: string
  evaluationStars: number | null
  assignmentType: string
  assignmentBy: string
  involvedAgents: string[]
  messages: any[]
}

/**
 * Gera o documento HTML autocontido (mesmo molde visual de buildConvHtml —
 * cabeçalho vermelho ISBET, cartões de estatística, transcript em bolhas) do
 * detalhe do Relatório de Resolução: os dados da aba "Detalhes" do
 * ResolutionDetailModal (classificação, avaliação CSAT, atribuição) + a linha
 * do tempo completa da conversa.
 */
export function buildResolutionDetailHtml(data: ResolutionDetailExportData): string {
  const e = escapeHtml
  const exportedAt = new Date().toLocaleString('pt-BR')
  const statusLbl  = convStatusLabel(data.status)
  const statusClass = { resolved: 'status-resolved', open: 'status-open', pending: 'status-pending', snoozed: 'status-snoozed' }[data.status] || 'status-open'
  const openedAt   = data.createdAt ? fmtDateFull(data.createdAt) : '—'
  const resolvedAt = data.status === 'resolved' && data.resolvedAt ? fmtDateFull(data.resolvedAt) : null
  const msgsHtml   = buildTimelineHtml(data.messages, data.contactName)

  const starsHtml = data.evaluationStars
    ? `<span class="stars-line">${'★'.repeat(data.evaluationStars)}${'☆'.repeat(5 - data.evaluationStars)}</span> <span class="stars-n">(${data.evaluationStars}/5)</span>`
    : '<span class="detail-empty">Sem avaliação</span>'

  const agentsHtml = data.involvedAgents.length
    ? data.involvedAgents.map(name => `<span class="chip">${e(name)}</span>`).join('')
    : '<span class="detail-empty">Nenhum agente identificado</span>'

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Relatório de Resolução — Conversa #${data.convId} — ISBET</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;background:#f4f5f7;color:#1f2937;line-height:1.5}
.page-wrap{max-width:780px;margin:0 auto;padding:28px 16px 56px}
/* Header */
.page-header{background:#BC0C24;color:#fff;border-radius:14px;padding:24px 28px;margin-bottom:20px}
.brand{display:flex;align-items:center;gap:10px;margin-bottom:16px}
.brand-name{font-size:18px;font-weight:800;letter-spacing:1px}
.brand-sub{font-size:10px;background:rgba(255,255,255,.2);padding:2px 9px;border-radius:20px;font-weight:600}
.conv-title{font-size:22px;font-weight:700;margin-bottom:14px}
.conv-id-tag{background:rgba(255,255,255,.2);padding:2px 10px;border-radius:6px;font-size:18px}
.meta-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(155px,1fr));gap:10px}
.meta-item{background:rgba(255,255,255,.12);border-radius:10px;padding:10px 13px}
.meta-lbl{font-size:9px;text-transform:uppercase;letter-spacing:1px;opacity:.65;margin-bottom:3px;font-weight:600}
.meta-val{font-size:13px;font-weight:600}
.status-badge{display:inline-block;padding:2px 10px;border-radius:20px;font-size:11px;font-weight:700;text-transform:uppercase}
.status-resolved{background:#dcfce7;color:#166534}
.status-open{background:#dbeafe;color:#1e40af}
.status-pending{background:#fef9c3;color:#854d0e}
.status-snoozed{background:#f3f4f6;color:#6b7280}
/* Detail cards (aba Detalhes do modal) */
.section-title{font-size:13px;font-weight:700;color:#374151;margin:20px 0 8px}
.detail-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:10px;margin-bottom:8px}
.detail-box{background:#fff;border-radius:10px;padding:10px 13px;box-shadow:0 1px 3px rgba(0,0,0,.07)}
.detail-lbl{font-size:9px;text-transform:uppercase;letter-spacing:1px;color:#9ca3af;margin-bottom:3px;font-weight:700}
.detail-val{font-size:13.5px;font-weight:600;color:#1f2937}
.detail-empty{font-size:12px;color:#9ca3af;font-style:italic}
.stars-line{color:#F59E0B;font-size:15px;letter-spacing:1px}
.stars-n{font-size:11px;color:#6b7280}
.chips{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:20px}
.chip{background:#f3f4f6;color:#374151;font-size:12px;padding:4px 11px;border-radius:999px;font-weight:500}
/* Timeline */
.timeline{display:flex;flex-direction:column;gap:4px}
.date-sep{text-align:center;margin:18px 0 10px}
.date-sep span{background:#e5e7eb;color:#6b7280;font-size:11px;padding:4px 14px;border-radius:20px;font-weight:600}
.msg-row{display:flex;align-items:flex-end;gap:8px;max-width:78%}
.msg-row--in{align-self:flex-start}
.msg-row--out{align-self:flex-end;flex-direction:row-reverse}
.bubble-av{width:30px;height:30px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;color:#fff;font-size:11px;font-weight:700}
.bubble-av--img{object-fit:cover}
.bubble{padding:9px 13px;border-radius:14px;max-width:100%;word-break:break-word}
.bubble--in{background:#fff;border-radius:14px 14px 14px 3px;box-shadow:0 1px 3px rgba(0,0,0,.07)}
.bubble--out{background:#BC0C24;color:#fff;border-radius:14px 14px 3px 14px}
.bubble-sender{font-size:10px;font-weight:700;margin-bottom:3px;opacity:.65}
.bubble-content{font-size:13.5px;line-height:1.55}
.bubble-time{font-size:10px;opacity:.5;margin-top:5px;text-align:right}
.att-img-wrap{margin-top:6px;border-radius:8px;overflow:hidden;max-width:260px}
.att-img{width:100%;display:block}
.att-file{display:inline-flex;align-items:center;gap:6px;background:rgba(0,0,0,.07);border-radius:6px;padding:5px 10px;font-size:12px;margin-top:5px}
.att-file a{color:inherit}
.bubble--out .att-file{background:rgba(255,255,255,.18)}
.msg-activity{align-self:center;text-align:center;margin:6px 0}
.activity-text{background:rgba(0,0,0,.06);color:#6b7280;font-size:11.5px;padding:4px 14px;border-radius:20px;display:inline-block}
.activity-time{display:block;font-size:10px;color:#9ca3af;margin-top:3px}
.page-footer{text-align:center;margin-top:36px;font-size:11px;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:16px}
@media print{
  body{background:#fff}
  .page-wrap{padding:0;max-width:100%}
  .page-header{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .bubble--out{-webkit-print-color-adjust:exact;print-color-adjust:exact}
}
</style>
</head>
<body>
<div class="page-wrap">

  <div class="page-header">
    <div class="brand">
      <span class="brand-name">ISBET</span>
      <span class="brand-sub">Relatório de Resolução</span>
    </div>
    <div class="conv-title">Conversa <span class="conv-id-tag">#${data.convId}</span></div>
    <div class="meta-grid">
      <div class="meta-item">
        <div class="meta-lbl">Status</div>
        <div class="meta-val"><span class="status-badge ${statusClass}">${statusLbl}</span></div>
      </div>
      <div class="meta-item">
        <div class="meta-lbl">Contato</div>
        <div class="meta-val">${e(data.contactName)}</div>
      </div>
      ${data.teamName ? `<div class="meta-item"><div class="meta-lbl">Equipe</div><div class="meta-val">${e(data.teamName)}</div></div>` : ''}
      <div class="meta-item">
        <div class="meta-lbl">Aberto em</div>
        <div class="meta-val">${e(openedAt)}</div>
      </div>
      ${resolvedAt ? `<div class="meta-item"><div class="meta-lbl">Resolvido em</div><div class="meta-val">${e(resolvedAt)}</div></div>` : ''}
    </div>
  </div>

  <div class="section-title">Detalhes</div>
  <div class="detail-grid">
    <div class="detail-box"><div class="detail-lbl">Resolução</div><div class="detail-val">${e(data.categoryLabel)}</div></div>
    <div class="detail-box"><div class="detail-lbl">Categoria</div><div class="detail-val">${e(data.bizCategoryLabel)}</div></div>
    <div class="detail-box"><div class="detail-lbl">Tempo total</div><div class="detail-val">${e(data.totalDuration)}</div></div>
    <div class="detail-box"><div class="detail-lbl">Maior intervalo sem resposta</div><div class="detail-val">${e(data.longestGap)}</div></div>
    <div class="detail-box"><div class="detail-lbl">Agente atual</div><div class="detail-val">${e(data.assigneeName || 'Sem agente')}</div></div>
    <div class="detail-box"><div class="detail-lbl">Avaliação</div><div class="detail-val">${starsHtml}</div></div>
    <div class="detail-box"><div class="detail-lbl">Tipo de atribuição</div><div class="detail-val">${e(data.assignmentType)}</div></div>
    <div class="detail-box"><div class="detail-lbl">Atribuído por</div><div class="detail-val">${e(data.assignmentBy)}</div></div>
  </div>

  <div class="section-title">Agentes envolvidos</div>
  <div class="chips">${agentsHtml}</div>

  <div class="section-title">Linha do tempo</div>
  <div class="timeline">
    ${msgsHtml}
  </div>

  <div class="page-footer">
    Exportado em ${exportedAt} &nbsp;·&nbsp; ISBET Wootdash CRM &nbsp;·&nbsp; Conversa #${data.convId}
    <br><small>Para salvar como PDF: Ctrl+P → Salvar como PDF</small>
  </div>

</div>
</body>
</html>`
}

/** Dispara o download de um arquivo HTML no navegador (Blob + link temporário). */
export function downloadHtmlFile(html: string, filename: string): void {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href     = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
